/**
 * PATH: packages/email-hub/src/feature/lib/smartSearchFlag.ts
 * FILE_ID: EMAILHUB-FEATURE-LIB-SMARTSEARCHFLAG-001
 * PURPOSE: User-facing "Smart search" opt-in (G3) ? when ON, inbox
 *          searches request rank=2 (RRF-fused lexical+semantic,
 *          be!4448) instead of the fe!5642 default rank=1.
 * WHY: Semantic search shipped server-side but had zero FE surface.
 *      LANDMINE: the operator kill switch (qt-search-rank-disabled,
 *      searchRankFlag in @quotech/ui) must keep winning ? when it is
 *      set, we send NO rank param at all, even if the user toggled
 *      Smart search on. Read per-call, never memoised (same contract
 *      as getSearchRankParam).
 * RELATED:
 *  - @quotech/ui searchRankFlag.ts: kill switch + rank=1 default.
 *  - hooks/useEmails.ts queryString: consumes search_rank override.
 */

import { getSearchRankParam } from '@quotech/ui'

/** localStorage key persisting the user's Smart-search toggle. */
export const SMART_SEARCH_KEY = 'qt-email-smart-search'

/** f(storage) ? whether the user has Smart search toggled on. */
export function isSmartSearchEnabled(): boolean {
  try {
    if (typeof window === 'undefined') return false
    return window.localStorage.getItem(SMART_SEARCH_KEY) === '1'
  } catch {
    return false
  }
}

/** Persist the toggle. Storage failures are silent (toggle stays
 *  ephemeral for the tab ? the UI state is the source of truth). */
export function setSmartSearchEnabled(enabled: boolean): void {
  try {
    if (enabled) window.localStorage.setItem(SMART_SEARCH_KEY, '1')
    else window.localStorage.removeItem(SMART_SEARCH_KEY)
  } catch {
    /* storage unavailable ? ignore */
  }
}

/**
 * f(toggle state, kill-switch state) ? the `rank` override to pass as
 * ``search_rank`` on the email list params, or undefined to fall back
 * to the fe!5642 default path (getSearchRankParam ? rank=1).
 *
 *  - toggle OFF            ? undefined (default path, rank=1 exactly
 *    as fe!5642 left it ? pinned in useEmails.rank.test.ts)
 *  - toggle ON             ? '2' (hybrid semantic)
 *  - toggle ON + kill switch set ? undefined override AND the default
 *    path also yields no rank (getSearchRankParam returns undefined),
 *    so no rank param is sent at all. Operator lever always wins.
 */
export function smartSearchRankParam(enabled: boolean): '2' | undefined {
  if (!enabled) return undefined
  // Kill switch check: getSearchRankParam() returns undefined when the
  // operator disabled ranking ? semantic re-rank must respect it too.
  if (getSearchRankParam() === undefined) return undefined
  return '2'
}
